
import React, { useState } from "react";
import { Download, FileText, CheckSquare, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { contactFormSchema, ContactFormValues, isNonFreeEmail } from "@/utils/forms/contactFormSchema";
import { recordResourceDownload } from "@/services/insightResourceService";

interface ResourceCardProps {
  title: string;
  description?: string;
  imageUrl?: string;
  filePath: string;
  isGated: boolean;
  resourceType: string;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ 
  title, 
  description, 
  imageUrl, 
  filePath, 
  isGated,
  resourceType 
}) => {
  const [showForm, setShowForm] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      title: "",
    },
  });

  const handleDownloadClick = () => {
    if (isGated) {
      setShowForm(true);
    } else {
      downloadResource();
    }
  };

  const downloadResource = () => {
    try {
      const link = document.createElement('a');
      link.href = `https://deuydlsjxvufcpxyldwt.supabase.co/storage/v1/object/public/resources/${filePath}`;
      link.download = title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Download started",
        description: "Your file download has started",
      });
    } catch (error) {
      console.error("Download error:", error);
      toast({
        title: "Download failed",
        description: "There was a problem downloading the file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (data: ContactFormValues) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Attempt to record the download
      await recordResourceDownload(
        data.firstName,
        data.lastName,
        data.email,
        data.company,
        data.title,
        title
      );
      
      // Even if the recording fails, we still want to allow the download
      setDownloadReady(true);
      setShowForm(false);
      downloadResource();
    } catch (error) {
      console.error("Form submission error:", error);
      
      // Allow download anyway in case of backend error
      toast({
        title: "Download available",
        description: "We had an issue recording your information, but your download is available.",
      });
      
      setDownloadReady(true);
      setShowForm(false);
      downloadResource();
    } finally {
      setIsSubmitting(false);
    }
  };

  const getResourceIcon = () => {
    switch (resourceType) {
      case 'checklist':
        return <CheckSquare className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />;
      case 'guide':
        return <FileText className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />;
      default:
        return <FileCheck className="h-5 w-5 text-techne-primaryPurple flex-shrink-0" />;
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-full flex flex-col">
        {imageUrl && (
          <div className="relative">
            <img 
              src={imageUrl} 
              alt={title} 
              className="w-full h-40 object-cover"
            />
            <div className="absolute top-2 right-2 bg-techne-purple text-white text-xs py-1 px-2 rounded-full">
              {resourceType.charAt(0).toUpperCase() + resourceType.slice(1)}
            </div>
          </div>
        )}
        
        <div className="p-5 flex flex-col flex-grow">
          <div className="flex items-start gap-3 mb-3">
            {getResourceIcon()}
            <h3 className="text-lg font-semibold">{title}</h3>
          </div>
          
          {description && (
            <p className="text-gray-600 text-sm mb-4">{description}</p>
          )}
          
          <Button 
            onClick={handleDownloadClick}
            className="mt-auto bg-techne-purple hover:bg-techne-deepPurple flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download Resource
          </Button>
        </div>
      </div>
      
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download {title}</DialogTitle>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Smith" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@company.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Corporation" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Role</FormLabel>
                    <FormControl>
                      <Input placeholder="Board Member, CEO, etc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Processing..." : "Download Resource"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResourceCard;
